import { FCElement } from '@fc/component';
import { trapFocus, removeTrapFocus, findFocusableElements } from '@fc/focus';
import {
  bindSurfaceSession,
  dispatchHeaderSurfaceState,
  getDetailsPanel,
  getDetailsSummary,
  requestDrawerSwitch,
  reserveViewportLock,
  releaseViewportLock,
  syncDetailsDisclosure,
} from '@fc/dialog';
import { onAnimationEnd } from '@fc/utilities';

class HeaderDrawer extends FCElement {
  requiredRefs = ['details'];
  #ac = null;
  #openController = null;
  #rootTrigger = null;
  #suppressSummaryClick = false;

  connectedCallback() {
    super.connectedCallback();

    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    const summary = getDetailsSummary(details);

    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    document.addEventListener('fit:drawer-close-immediate', this.#onDrawerSwitch, { signal });

    if (summary instanceof HTMLElement && details.dataset.headerSurface !== 'menu') {
      summary.addEventListener('keydown', this.handleSummaryKeydown, { signal });
    }

    this.#bindAnimatedElements(signal);
    syncDetailsDisclosure(details, details.open);
  }

  disconnectedCallback() {
    if (this.refs.details?.open) {
      dispatchHeaderSurfaceState({ source: this.refs.details, owner: this, phase: 'deactivate' });
    }

    this.#removeOpenSession();
    removeTrapFocus(this.refs.details);
    releaseViewportLock(this.refs.details);
    this.#ac?.abort();
    this.#ac = null;
    super.disconnectedCallback();
  }

  // Whether the page is in bar (Interactive Island) mode on desktop.
  get #isBarMode() {
    return window.innerWidth >= 750 && this.hasAttribute('data-bar-mode');
  }

  get isOpen() {
    return this.refs.details?.open ?? false;
  }

  #removeOpenSession() {
    this.#openController?.abort();
    this.#openController = null;
  }

  #detailsFor(event, options = {}) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    const selector = options.onlyOpen ? 'details[open]' : 'details';
    const found = event.target.closest(selector);
    return found instanceof HTMLDetailsElement ? found : this.refs.details;
  }

  #getLocalizationForm(details = this.refs.details) {
    if (!(details instanceof HTMLDetailsElement)) return null;

    const forms = details.querySelectorAll('locale-form[data-localization-style="drawer"]');
    for (const form of forms) {
      if (!(form instanceof HTMLElement)) continue;
      if (form.closest('details') === details) return form;
    }

    return null;
  }

  #getInitialFocus(details) {
    const summary = getDetailsSummary(details);
    if (!(summary instanceof HTMLElement)) return null;

    const localizationForm = this.#getLocalizationForm(details);
    const primaryControl =
      localizationForm?.querySelector('[data-ref="search"]') instanceof HTMLElement
        ? localizationForm.querySelector('[data-ref="search"]')
        : localizationForm?.querySelector('[data-ref="countryList"]') instanceof HTMLElement
          ? localizationForm.querySelector('[data-ref="countryList"]')
          : null;

    return primaryControl || details.querySelector('.menu-drawer__close-button, .menu-drawer__back-button') || summary;
  }

  #focusLocalizationForm(localizationForm, { focus = true, block = 'center' } = {}) {
    if (!(localizationForm instanceof HTMLElement)) return;

    const preparePrimaryControl = () => {
      if (focus && typeof localizationForm.focusPrimaryControl === 'function') {
        localizationForm.focusPrimaryControl({ block });
        return true;
      }

      if (!focus && typeof localizationForm.syncPrimaryControl === 'function') {
        localizationForm.syncPrimaryControl({ block });
        return true;
      }

      const fallback =
        localizationForm.querySelector('[data-ref="search"]') instanceof HTMLElement
          ? localizationForm.querySelector('[data-ref="search"]')
          : localizationForm.querySelector('[data-ref="countryList"]');
      if (fallback instanceof HTMLElement) {
        if (focus) {
          fallback.focus();
        }
        return true;
      }

      return false;
    };

    if (preparePrimaryControl()) return;

    customElements.whenDefined('locale-form').then(() => {
      if (!localizationForm.isConnected) return;
      preparePrimaryControl();
    });
  }

  #beginRootSession(initialFocus, options = {}) {
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    const {
      containFocus = !this.#isBarMode,
      allowEscape = !this.#isBarMode,
      focusInitial = containFocus,
      trapTarget = details,
    } = options;

    this.#removeOpenSession();
    this.#openController = bindSurfaceSession({
      host: this,
      surface: details,
      trapTarget,
      initialFocus,
      trapFocusWithin: containFocus,
      focusInitial,
      onEscape: allowEscape
        ? (event) => {
            this.#shut(this.#detailsFor(event, { onlyOpen: true }), { reason: 'escape-key' });
          }
        : undefined,
      closeOnOutsideClick: !this.#isBarMode,
      onOutsideClick: this.#isBarMode
        ? undefined
        : () => {
            this.close({ reason: 'outside-click' });
          },
    });

    if (containFocus && !this.#isBarMode) {
      this.#bindContainmentFallback(details, trapTarget, this.#openController?.signal);
    }
  }

  // Immediately close when another drawer is opening.
  #onDrawerSwitch = (event) => {
    if (event.detail?.except === this) return;
    if (!this.isOpen) return;

    this.#shut(this.refs.details, { restoreFocus: false, immediate: true, reason: 'drawer-switch' });
  };

  toggle(event) {
    if (event instanceof MouseEvent && this.#suppressSummaryClick) {
      event.preventDefault();
      this.#suppressSummaryClick = false;
      return;
    }

    this.#suppressSummaryClick = false;

    // Prevent native <details> toggle so JS fully controls the `open` attribute.
    // The FC framework fires this handler in the capture phase, before the
    // browser's default action. Without preventDefault, the native toggle runs
    // AFTER our handler: on close it re-adds `open` (causing stale border-radius),
    // on open it removes `open` (cancelling the open). Only toggle() is affected
    // because it's bound to <summary> via @click="/toggle". Submenu opens use
    // open() directly from <summary @click="nav-drawer/open"> and rely on the
    // native toggle to add `open`.
    if (event instanceof Event) event.preventDefault();

    if (this.isOpen) {
      this.close({ reason: 'toggle' });
    } else {
      // Manually set `open` since we prevented the native toggle.
      this.refs.details?.setAttribute('open', '');
      this.open();
    }
  }

  handleSummaryKeydown = (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    event.stopPropagation();
    this.#suppressSummaryClick = true;
    setTimeout(() => {
      this.#suppressSummaryClick = false;
    }, 0);

    this.toggle();
  };

  open(event) {
    const details = this.#detailsFor(event);
    const summary = getDetailsSummary(details);
    if (!(summary instanceof HTMLElement)) return;

    const localizationForm = this.#getLocalizationForm(details);
    const shouldContainFocus = !this.#isBarMode || localizationForm instanceof HTMLElement;
    const trapTarget =
      localizationForm instanceof HTMLElement && this.#isBarMode ? getDetailsPanel(details) || details : details;
    const initialFocus =
      this.#isBarMode && localizationForm instanceof HTMLElement ? undefined : this.#getInitialFocus(details);

    // Close other drawers when opening the root menu (not submenus).
    if (details === this.refs.details) {
      if (!details.open) {
        details.setAttribute('open', '');
      }

      this.#rootTrigger = summary;

      if (!this.#isBarMode && details.hasAttribute('scroll-lock')) {
        reserveViewportLock(details);
      }

      dispatchHeaderSurfaceState({ source: details, owner: this, phase: 'activate' });
      requestDrawerSwitch(this);
      syncDetailsDisclosure(details, true);

      requestAnimationFrame(() => {
        details.classList.add('menu-open');

        setTimeout(() => {
          this.#beginRootSession(initialFocus, {
            containFocus: shouldContainFocus,
            allowEscape: shouldContainFocus,
            trapTarget,
          });
          if (localizationForm instanceof HTMLElement) {
            requestAnimationFrame(() =>
              this.#focusLocalizationForm(localizationForm, {
                focus: !this.#isBarMode,
                block: 'center',
              }),
            );
          }
        }, 0);
      });
      return;
    }

    syncDetailsDisclosure(details, true);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        if (this.#isBarMode) {
          if (initialFocus instanceof HTMLElement && details.contains(initialFocus)) {
            initialFocus.focus();
          }
        } else {
          trapFocus(details, { initialFocus });
        }
        requestAnimationFrame(() => this.#focusLocalizationForm(localizationForm));
      }, 0);
    });
  }

  #bindContainmentFallback(details, trapTarget, signal) {
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!(trapTarget instanceof HTMLElement)) return;
    if (!(signal instanceof AbortSignal)) return;

    let tabDirection = 1;

    const focusEdge = (reverse = false) => {
      const focusableElements = findFocusableElements(trapTarget);
      const next = reverse ? focusableElements.at(-1) : focusableElements[0];
      if (next instanceof HTMLElement) next.focus();
    };

    document.addEventListener(
      'keydown',
      (event) => {
        if (!(event instanceof KeyboardEvent) || event.key !== 'Tab') return;
        if (!details.open) return;

        const focusableElements = findFocusableElements(trapTarget);
        if (!focusableElements.length) return;

        tabDirection = event.shiftKey ? -1 : 1;
        const activeElement = document.activeElement;

        if (!(activeElement instanceof HTMLElement) || !trapTarget.contains(activeElement)) {
          event.preventDefault();
          focusEdge(event.shiftKey);
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements.at(-1);

        if (!event.shiftKey && activeElement === lastElement) {
          event.preventDefault();
          focusEdge(false);
          return;
        }

        if (event.shiftKey && activeElement === firstElement) {
          event.preventDefault();
          focusEdge(true);
        }
      },
      { capture: true, passive: false, signal },
    );

    document.addEventListener(
      'focusin',
      (event) => {
        if (!details.open) return;
        if (event.target instanceof Node && trapTarget.contains(event.target)) return;

        focusEdge(tabDirection < 0);
      },
      { capture: true, signal },
    );
  }

  back(event) {
    this.#shut(this.#detailsFor(event, { onlyOpen: true }), { reason: 'back-button' });
  }

  close(options = {}) {
    this.#shut(this.refs.details, options);
  }

  dismiss(options = {}) {
    const openDetails = [...this.querySelectorAll('details[open]')].reverse().find((details) => details instanceof HTMLDetailsElement);
    this.#shut(openDetails || this.refs.details, options);
  }

  #shut(details, options = {}) {
    if (!(details instanceof HTMLDetailsElement)) return;

    const summary = getDetailsSummary(details);
    if (!(summary instanceof HTMLElement)) return;
    const { restoreFocus = true, immediate = false } = options;

    if (details === this.refs.details) {
      this.#removeOpenSession();
    }

    syncDetailsDisclosure(details, false);
    details.classList.remove('menu-open');

    const finishClose = () => {
      reset(details);

      if (details === this.refs.details) {
        dispatchHeaderSurfaceState({ source: details, owner: this, phase: 'deactivate' });
        removeTrapFocus(details);
        releaseViewportLock(details);

        for (const sub of this.querySelectorAll('details[open]')) {
          if (sub instanceof HTMLDetailsElement && sub !== details) {
            reset(sub);
          }
        }

        if (restoreFocus && this.#rootTrigger?.isConnected) {
          this.#rootTrigger.focus();
        }
        return;
      }

      // Closing a submenu either restores the root trap (modal/mobile) or
      // returns focus to the opener without trapping (desktop bar mode).
      setTimeout(() => {
        if (this.#isBarMode) {
          summary.focus();
          return;
        }

        trapFocus(this.refs.details, { initialFocus: summary });
      }, 0);
    };

    if (immediate) {
      finishClose();
      return;
    }

    onAnimationEnd(details, finishClose);
  }

  #bindAnimatedElements(signal) {
    const clearWillChange = (event) => {
      const element = event.target;
      if (element instanceof HTMLElement) {
        element.style.setProperty('will-change', 'unset');
        element.removeEventListener('animationend', clearWillChange);
      }
    };

    for (const element of this.querySelectorAll('.menu-drawer__animated-element')) {
      if (element instanceof HTMLElement) {
        element.addEventListener('animationend', clearWillChange, { signal, passive: true });
      }
    }
  }
}

if (!customElements.get('nav-drawer')) {
  customElements.define('nav-drawer', HeaderDrawer);
}

function reset(element) {
  if (!(element instanceof HTMLDetailsElement)) return;

  element.classList.remove('menu-open');
  element.removeAttribute('open');
  syncDetailsDisclosure(element, false);
}
